/**
 * Produto — visão das duas camadas (Task 11, brief 3.2) · redesign Entrega 3.
 *
 * Tudo o que o brief pede num só lugar, com a ORIGEM de cada dado visível:
 * SKU/variações/kit/categoria/fornecedor/custo/preço-base/fiscal (referência
 * Bling, somente-leitura com selo), camada comercial (editável pela
 * Plataforma), tabelas B2B, disponibilidade consolidada com as reservas que a
 * reduziram, divergências DESTE item campo a campo e o histórico de alterações.
 *
 * Quem decide editável vs somente-leitura é `packages/catalog` (espelho das
 * migrations 0019/0030, promovido na Fase 2 do Plano de Ataque R8 — ver
 * ADR-0005) — nunca esta tela.
 *
 * Redesign (apresentação apenas): layout de "detalhe" da base de UI
 * internalizada (ver THIRD-PARTY-NOTICES na raiz do repo) — cada bloco é
 * uma SEÇÃO em card com hierarquia de título; a camada operacional (Bling) traz
 * um SELO DE AUTORIDADE "somente-leitura" no topo e estilo visualmente distinto
 * (fundo suave + traço neutro) da camada comercial editável — read-only x
 * editável nunca é só cor, é selo + estilo + texto. Todos os `data-block` e
 * demais hooks dos testes PRESERVADOS.
 *
 * Migração para `.tsx` (Cluster B — Catálogo & Estoque, redesign por perfil):
 * mesmo markup/contrato de antes, só a sintaxe `createElement` → JSX. Import
 * de `React` explícito — ver nota equivalente em `layout/AppShell.tsx`.
 * `block()` passa a receber `children` como UM `ReactNode` (Fragment no
 * chamador) em vez de `ReactNode[]` espalhado — evita o aviso de "key" do
 * React para array de filhos sem key; o markup/atributos renderizados são
 * idênticos (Fragment não aparece no DOM).
 */
import React, { useState, type ReactElement, type ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, CircleAlert, CircleCheck, Lock, Package as PackageIcon } from "lucide-react";
import { Badge, Tabs } from "../ui/index.js";
import { CatalogBoundary } from "../components/CatalogBoundary.js";
import { RecordTable } from "../components/RecordTable.js";
import { StatusPanel } from "../components/StatusPanel.js";
import { AuthorityBadge } from "../components/AuthorityBadge.js";
import { AvailabilityBadge, PublishedBadge } from "../components/Badges.js";
import { AiEnrichmentPanel, type AiEnrichmentHandlers } from "../components/AiEnrichmentPanel.js";
import { CommercialEditForm, type CommercialEditHandlers } from "../components/CommercialEditForm.js";
import { MediaEditForm, type MediaEditHandlers } from "../components/MediaEditForm.js";
import { DivergenciasTable } from "./Divergencias.js";
import { divergencesForProduct, summarizeDivergences, fieldLabel } from "../../../../packages/catalog/src/index.js";
import { availabilityOfProduct, currentPriceCentsOf } from "../catalog/availability.js";
import { availabilityBucketOf } from "../catalog/list-view.js";
import { blingSourceLabel, catalogDivergencesOf, catalogProductById, type CatalogDataset } from "../catalog/dataset.js";
import { formatBrl, formatCount, formatDateTime } from "../format.js";
import { useCommercialLayerUpdate, useProductEnrichmentActions, useProductEnrichments, useProductMediaAdd, useProductMediaRemove, useProductMediaUpdate } from "../queries.js";
import type { CatalogProductViewProps } from "../catalog/view-props.js";
import type {
  CatalogChangeHistoryEntry,
  CatalogKitItem,
  CatalogVariant,
  PlatformCatalogProduct,
  StockReservation
} from "../api/types.js";

/** Casca de seção (card da base de UI): título + conteúdo, com os hooks de bloco. */
function block(dataBlock: string, title: ReactNode, children: ReactNode, opts?: { operational?: boolean }): ReactElement {
  return (
    <section
      className={
        "product-block flex flex-col gap-4 rounded-panel border border-border bg-surface p-5 shadow-s1" +
        (opts?.operational ? "" : "")
      }
      data-block={dataBlock}
    >
      <h3 className="flex flex-wrap items-center gap-2 text-lg font-extrabold text-ink">{title}</h3>
      {children}
    </section>
  );
}

/** Linha do ERP: rótulo à esquerda, valor à direita. Somente leitura (a origem vai uma vez no cabeçalho do cartão). */
function readOnlyField(fieldKey: string, value: ReactElement | string): ReactElement {
  return (
    <div
      key={fieldKey}
      className="field-row flex items-start justify-between gap-4 border-b border-border py-2.5 text-sm last:border-b-0"
      data-field-key={fieldKey}
      data-editable="false"
    >
      <dt className="shrink-0 text-muted">{fieldLabel(fieldKey)}</dt>
      <dd className="m-0 min-w-0 break-words text-right font-bold text-ink">{value}</dd>
    </div>
  );
}

function operationalBlock(product: PlatformCatalogProduct): ReactElement {
  const operational = product.operational;
  const fiscal = operational.fiscalData;
  return (
    <section className="product-block flex flex-col gap-3 rounded-panel border border-border bg-surface p-5 shadow-s1" data-block="operacional">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 text-lg font-extrabold text-ink">
          <Lock className="h-4 w-4 text-muted" aria-hidden focusable={false} />
          Dados do ERP
        </h3>
        <AuthorityBadge fieldKey="sku" />
      </div>
      <p className="m-0 text-xs text-muted">Vêm do Bling e não são editados aqui. Para mudar, altere no ERP e sincronize.</p>
      <dl className="m-0">
        {readOnlyField("sku", operational.sku)}
        {readOnlyField("category", operational.category ?? "—")}
        {readOnlyField("supplier", operational.supplier ?? "—")}
        {readOnlyField("cost", operational.costCents === null ? "custo não sincronizado" : formatBrl(operational.costCents))}
        {readOnlyField(
          "base_price",
          operational.basePriceCents === null || operational.basePriceCents === 0 ? "sem preço-base" : formatBrl(operational.basePriceCents)
        )}
        {readOnlyField("stock", operational.stock === null ? "estoque não sincronizado" : formatCount(operational.stock))}
        {readOnlyField(
          "fiscal_data",
          fiscal ? `NCM ${fiscal.ncm} · origem ${fiscal.origin} · CFOP ${fiscal.cfop}` : "sem dados fiscais sincronizados"
        )}
      </dl>
      <p className="page__note m-0 rounded-control bg-surface-soft px-3 py-2 text-xs text-muted">
        {operational.blingId
          ? `Vínculo Bling: ${operational.blingId}. Última sincronização: ${operational.lastSyncedAt ? formatDateTime(operational.lastSyncedAt) : "nunca"}.`
          : "Produto SEM vínculo no Bling: nenhum dado operacional é sincronizado para este item."}
      </p>
    </section>
  );
}

function variantsBlock(product: PlatformCatalogProduct): ReactElement {
  return block(
    "variacoes",
    <>
      Variações <AuthorityBadge fieldKey="variants" />
    </>,
    <RecordTable<CatalogVariant>
      bare
      caption="Variações/SKUs filhos do produto"
      rows={product.operational.variants}
      idOf={(variant) => variant.sku}
      emptyMessage="Produto sem variações no operacional."
      columns={[
        { key: "sku", label: "SKU", render: (variant) => variant.sku },
        { key: "label", label: "Variação", render: (variant) => variant.label },
        { key: "stock", label: "Estoque", align: "right", render: (variant) => formatCount(variant.stock) }
      ]}
    />,
    { operational: true }
  );
}

function kitBlock(product: PlatformCatalogProduct): ReactElement {
  return block(
    "kit",
    <>
      Composição do kit <AuthorityBadge fieldKey="kit_composition" />
    </>,
    <RecordTable<CatalogKitItem>
      bare
      caption="SKUs que compõem o kit"
      rows={product.operational.kitItems}
      idOf={(item) => item.sku}
      emptyMessage="Produto não é kit (sem composição)."
      columns={[
        { key: "sku", label: "SKU do componente", render: (item) => item.sku },
        { key: "quantity", label: "Quantidade", align: "right", render: (item) => formatCount(item.quantity) }
      ]}
    />,
    { operational: true }
  );
}

function EquationTerm({ label, value, strong }: { label: string; value: number; strong?: boolean }): ReactElement {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs font-extrabold text-muted">{label}</span>
      <span className={"font-black leading-none tabular-nums text-ink " + (strong ? "text-[2rem]" : "text-2xl")}>{formatCount(value)}</span>
    </div>
  );
}

function availabilityBlock(product: PlatformCatalogProduct): ReactElement {
  const availability = availabilityOfProduct(product);
  const price = currentPriceCentsOf(product);
  const detail =
    availability.sellableStock === null
      ? "Sem oferta vendável vigente: disponibilidade 0 (não há o que vender, independente do estoque operacional)."
      : `Disponibilidade consolidada: ${formatCount(availability.consolidated)} = ${formatCount(availability.sellableStock)} vendável(is) − ${formatCount(availability.activeReservations)} reservado(s).`;
  return block(
    "disponibilidade",
    <>
      Disponibilidade e reservas <AuthorityBadge fieldKey="availability" />
    </>,
    <>
      <div
        className="product-block__highlight flex flex-col gap-4"
        data-availability={availability.consolidated}
        data-critical={availability.critical ? "true" : "false"}
      >
        <div className="w-fit">
          <AvailabilityBadge
            bucket={availability.sellableStock === null ? "indisponivel" : availabilityBucketOf(product)}
            consolidated={availability.sellableStock === null ? null : availability.consolidated}
            critical={availability.critical}
          />
        </div>
        {availability.sellableStock === null ? null : (
          <div className="flex flex-wrap items-end gap-6" aria-label="Cálculo da disponibilidade">
            <EquationTerm label="Estoque vendável" value={availability.sellableStock} />
            <span className="pb-1 text-2xl font-black text-muted" aria-hidden>−</span>
            <EquationTerm label="Reservado" value={availability.activeReservations} />
            <span className="pb-1 text-2xl font-black text-muted" aria-hidden>=</span>
            <EquationTerm label="Disponível" value={availability.consolidated} strong />
          </div>
        )}
        <p className="m-0 text-sm text-muted">{detail}</p>
      </div>
      <p className="page__note text-sm text-muted">{price === null ? "Sem preço vigente." : `Preço vigente: ${formatBrl(price)}.`}</p>
      <RecordTable<StockReservation>
        bare
      caption="Reservas do produto"
        rows={product.reservations}
        idOf={(reservation) => reservation.id}
        emptyMessage="Nenhuma reserva registrada para este produto."
        columns={[
          { key: "id", label: "Reserva", render: (reservation) => reservation.id },
          { key: "quantity", label: "Quantidade", align: "right", render: (reservation) => formatCount(reservation.quantity) },
          { key: "status", label: "Situação", render: (reservation) => reservation.status },
          { key: "reason", label: "Motivo", render: (reservation) => reservation.reason },
          {
            key: "expiresAt",
            label: "Expira em",
            render: (reservation) => (reservation.expiresAt ? formatDateTime(reservation.expiresAt) : "sem expiração")
          }
        ]}
      />
    </>
  );
}

function b2bBlock(product: PlatformCatalogProduct): ReactElement {
  const rows = product.b2bPriceTables.flatMap((table) =>
    table.entries.map((entry) => ({
      id: `${table.id}_${entry.minQuantity}`,
      table: table.name,
      companyId: table.companyId,
      active: table.active,
      ...entry
    }))
  );
  return block(
    "b2b",
    <>
      Tabelas de preço B2B <AuthorityBadge fieldKey="b2b_pricing" />
    </>,
    <RecordTable<(typeof rows)[number]>
      bare
      caption="Faixas de preço por empresa aprovada"
      rows={rows}
      idOf={(row) => row.id}
      emptyMessage="Nenhuma tabela B2B aplicável a este produto."
      columns={[
        { key: "table", label: "Tabela", render: (row) => row.table },
        { key: "company", label: "Empresa", render: (row) => row.companyId ?? "todas as aprovadas" },
        { key: "minQuantity", label: "Quantidade mínima", align: "right", render: (row) => formatCount(row.minQuantity) },
        { key: "multiple", label: "Múltiplo de embalagem", align: "right", render: (row) => formatCount(row.packageMultiple) },
        { key: "price", label: "Preço", align: "right", render: (row) => formatBrl(row.priceCents) },
        { key: "active", label: "Ativa", render: (row) => (row.active ? "sim" : "não") }
      ]}
    />
  );
}

function mediaBlock(product: PlatformCatalogProduct, mediaEdit?: MediaEditHandlers): ReactElement {
  return block(
    "midia",
    <>
      Mídia e conteúdo técnico <AuthorityBadge fieldKey="media" />
    </>,
    <MediaEditForm product={product} edit={mediaEdit} />
  );
}

function historyBlock(product: PlatformCatalogProduct): ReactElement {
  const rows = [...product.changeHistory].sort((a, b) => b.changedAt.localeCompare(a.changedAt));
  return block(
    "historico",
    "Histórico de alterações",
    <RecordTable<CatalogChangeHistoryEntry>
      bare
      caption="Quem mudou o quê, por campo e por origem"
      rows={rows}
      idOf={(entry) => entry.id}
      emptyMessage="Nenhuma alteração registrada para este produto."
      columns={[
        { key: "changedAt", label: "Quando", render: (entry) => formatDateTime(entry.changedAt) },
        { key: "field", label: "Campo", render: (entry) => fieldLabel(entry.fieldKey) },
        { key: "previous", label: "Antes", render: (entry) => JSON.stringify(entry.previousValue ?? null) },
        { key: "new", label: "Depois", render: (entry) => JSON.stringify(entry.newValue ?? null) },
        {
          key: "source",
          label: "Origem",
          render: (entry) =>
            entry.source === "bling_sync"
              ? "sync do Bling"
              : entry.source === "platform_edit"
                ? `edição na Plataforma${entry.actor ? ` (${entry.actor})` : ""}`
                : "sistema"
        }
      ]}
    />
  );
}

export interface ProdutoDetalheViewProps extends CatalogProductViewProps {
  /** Injetado pela página; ausente = montagem somente-leitura (teste/SSR). */
  commercialEdit?: CommercialEditHandlers;
  /** Idem, para adicionar/reordenar/remover mídia (Plano Loja/Twotrees, Fase 2). */
  mediaEdit?: MediaEditHandlers;
  /** Idem, para o enriquecimento de cadastro com IA (OpenRouter); ausente = seção informa que não está montada. */
  aiEnrich?: AiEnrichmentHandlers;
}

export function ProdutoDetalheView({ dataset, productId, commercialEdit, mediaEdit, aiEnrich }: ProdutoDetalheViewProps): ReactElement {
  const product = catalogProductById(dataset, productId);
  if (!product) {
    return (
      <div className="page page--produto flex flex-col gap-4" data-page="produto-detalhe">
        <StatusPanel
          kind="empty"
          title="Produto não encontrado no catálogo da Plataforma"
          detail={`Nenhum produto com id ${productId}. Ele pode existir só no Bling — nesse caso aparece na tela de divergências como vínculo ausente.`}
        />
        <Link to="/divergencias" className="w-fit text-sm font-semibold text-brand-text hover:underline">
          Ver divergências de vínculo
        </Link>
      </div>
    );
  }
  const allDivergences = catalogDivergencesOf(dataset);
  const divergences = allDivergences ? divergencesForProduct(allDivergences, product.productId) : null;
  const summary = divergences ? summarizeDivergences(divergences) : null;
  return (
    <ProdutoAbas
      product={product}
      divergenceCount={summary?.total ?? null}
      commercialEdit={commercialEdit}
      mediaEdit={mediaEdit}
      aiEnrich={aiEnrich}
      divergencesPanel={
        divergences === null ? (
          <StatusPanel kind="awaiting_source" title="Sem o lado operacional não há comparação" detail={blingSourceLabel(dataset.snapshot)} />
        ) : (
          <div className="flex flex-col gap-3">
            <p className="page__note text-sm text-muted" data-divergence-total={summary?.total ?? 0}>
              {`${summary?.total ?? 0} divergência(s) campo a campo (${summary?.actionRequired ?? 0} exigindo ação). ` +
                blingSourceLabel(dataset.snapshot)}
            </p>
            <DivergenciasTable divergences={divergences} bare compact />
          </div>
        )
      }
    />
  );
}

/** Foto do produto; sem URL, ou se a imagem falhar ao carregar, mostra um espaço reservado (nunca um ícone quebrado). */
function ProductCover({ url, alt }: { url: string | null; alt: string }): ReactElement {
  const [failed, setFailed] = useState(false);
  return (
    <div className="grid aspect-square w-full max-w-[11rem] place-items-center overflow-hidden rounded-panel border border-border bg-surface-soft" data-product-cover={url && !failed ? "image" : "placeholder"}>
      {url && !failed ? (
        <img src={url} alt={alt} loading="lazy" onError={() => setFailed(true)} className="h-full w-full object-cover" />
      ) : (
        <div className="flex flex-col items-center gap-2 text-muted">
          <PackageIcon className="h-10 w-10" aria-hidden focusable={false} />
          <span className="text-xs font-bold">Sem foto</span>
        </div>
      )}
    </div>
  );
}

function HeroMetric({ label, value, kpi, hint, big, warn }: { label: string; value: string; kpi: string; hint?: string; big?: boolean; warn?: boolean }): ReactElement {
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <dt className="text-sm font-extrabold text-muted">{label}</dt>
      <dd className={"m-0 font-black leading-none tabular-nums " + (big ? "whitespace-nowrap text-[2rem] text-ink" : "whitespace-nowrap text-2xl text-ink")} data-kpi={kpi}>
        {value}
      </dd>
      {hint ? <span className={"text-xs " + (warn ? "font-bold text-warning" : "text-muted")}>{hint}</span> : null}
    </div>
  );
}

type ProdutoAba = "resumo" | "estoque" | "midia" | "divergencias" | "historico";

/**
 * Detalhe do produto em abas (redesign admin): a página única com oito painéis
 * empilhados virou Resumo · Estoque e preços · Mídia · Divergências · Histórico.
 * Todos os painéis continuam no HTML (só alternam `hidden`), então nenhum bloco
 * deixa de existir para teste/leitor de tela; o que muda é o que fica à vista.
 */
function ProdutoAbas({
  product,
  divergenceCount,
  commercialEdit,
  mediaEdit,
  aiEnrich,
  divergencesPanel
}: {
  product: PlatformCatalogProduct;
  divergenceCount: number | null;
  commercialEdit?: CommercialEditHandlers;
  mediaEdit?: MediaEditHandlers;
  aiEnrich?: AiEnrichmentHandlers;
  divergencesPanel: ReactElement;
}): ReactElement {
  const [tab, setTab] = useState<ProdutoAba>("resumo");
  const op = product.operational;
  const panel = (id: ProdutoAba, children: ReactNode): ReactElement => (
    <div key={id} role="tabpanel" aria-label={id} hidden={tab !== id} data-tab-panel={id} className="flex flex-col gap-6">
      {children}
    </div>
  );
  const price = currentPriceCentsOf(product);
  const marginPct = price !== null && price > 0 && op.costCents !== null ? ((price - op.costCents) / price) * 100 : null;
  const cover = product.commercial.media.find((m) => m.kind === "image") ?? null;
  const availability = availabilityOfProduct(product);
  return (
    <div className="page page--produto flex flex-col gap-6" data-page="produto-detalhe" data-product-id={product.productId}>
      <nav aria-label="Trilha" className="flex flex-wrap items-center gap-1 text-sm font-bold text-muted">
        <Link to="/catalogo" className="drilldown-header__back inline-flex items-center gap-1 hover:text-ink">
          <ArrowLeft className="h-4 w-4" aria-hidden focusable={false} />
          Catálogo
        </Link>
        {op.category ? (
          <>
            <span aria-hidden>/</span>
            <Link to={`/catalogo?categoria=${encodeURIComponent(op.category)}`} className="hover:text-ink">
              {op.category}
            </Link>
          </>
        ) : null}
      </nav>
      <section aria-label="Resumo do produto" className="grid grid-cols-1 gap-6 rounded-panel border border-border bg-surface p-5 shadow-s1 md:grid-cols-[11rem_minmax(0,1fr)]">
        <ProductCover url={cover?.url ?? null} alt={cover?.title ?? product.name} />
        <div className="flex min-w-0 flex-col gap-4">
          <div className="flex flex-col gap-2">
            <h2 className="text-2xl font-black leading-tight text-ink sm:text-3xl">{product.commercial.commercialName ?? product.name}</h2>
            <div className="drilldown-header__origin flex flex-wrap items-center gap-2 text-sm text-muted">
              <span className="font-bold">{`SKU ${op.sku}`}</span>
              <PublishedBadge published={product.commercial.published} />
              {op.supplier ? <Badge tone="neutral">{op.supplier}</Badge> : null}
              <span className="inline-flex items-center gap-1 text-xs" data-sync-status={op.blingId ? "linked" : "unlinked"}>
                {op.blingId ? <CircleCheck className="h-4 w-4 text-success" aria-hidden focusable={false} /> : <CircleAlert className="h-4 w-4 text-warning" aria-hidden focusable={false} />}
                {op.blingId
                  ? `Sincronizado com o Bling${op.lastSyncedAt ? ` em ${formatDateTime(op.lastSyncedAt)}` : ""}`
                  : "Sem vínculo no Bling"}
              </span>
            </div>
          </div>
          <dl className="m-0 grid grid-cols-2 gap-x-8 gap-y-4 border-t border-border pt-4 sm:grid-cols-[auto_auto_auto_1fr]">
            <HeroMetric label="Preço de venda" kpi="produto-preco" value={price === null ? "—" : formatBrl(price)} big />
            <HeroMetric label="Custo" kpi="produto-custo" value={op.costCents === null ? "—" : formatBrl(op.costCents)} />
            <HeroMetric
              label="Margem"
              kpi="produto-margem"
              value={marginPct === null ? "—" : `${marginPct.toFixed(1).replace(".", ",")}%`}
            />
            <HeroMetric label="Disponível" kpi="produto-disponivel" value={`${availability.consolidated} un.`} hint={availability.critical ? "Estoque crítico" : "Estoque menos reservas"} warn={availability.critical} />
          </dl>
        </div>
      </section>
      <Tabs<ProdutoAba>
        label="Seções do produto"
        active={tab}
        onChange={setTab}
        tabs={[
          { id: "resumo", label: "Resumo" },
          { id: "estoque", label: "Estoque e preços" },
          { id: "midia", label: "Mídia" },
          { id: "divergencias", label: "Divergências", count: divergenceCount ?? undefined },
          { id: "historico", label: "Histórico" }
        ]}
      />
      {panel(
        "resumo",
        <>
          <div className="grid grid-cols-1 items-start gap-6 xl:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
            <CommercialEditForm product={product} edit={commercialEdit} />
            {operationalBlock(product)}
          </div>
          <AiEnrichmentPanel ai={aiEnrich} />
        </>
      )}
      {panel(
        "estoque",
        <>
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            {variantsBlock(product)}
            {kitBlock(product)}
          </div>
          {availabilityBlock(product)}
          {b2bBlock(product)}
        </>
      )}
      {panel("midia", mediaBlock(product, mediaEdit))}
      {panel("divergencias", block("divergencias", "Divergências Bling↔local deste item", divergencesPanel))}
      {panel("historico", historyBlock(product))}
    </div>
  );
}

export function ProdutoDetalhe(): ReactElement {
  const { productId } = useParams();
  const mutation = useCommercialLayerUpdate();
  const addMedia = useProductMediaAdd();
  const updateMedia = useProductMediaUpdate();
  const removeMedia = useProductMediaRemove();
  const id = productId ?? "";
  const enrichments = useProductEnrichments(id);
  const enrichActions = useProductEnrichmentActions(id);
  const aiEnrich: AiEnrichmentHandlers = {
    drafts: enrichments.data ?? [],
    generate: (target, guidance) => enrichActions.generate.mutate({ target, guidance }),
    resolve: (enrichmentId, action) => enrichActions.resolve.mutate({ id: enrichmentId, action }),
    pending: enrichActions.generate.isPending || enrichActions.resolve.isPending,
    error: enrichActions.generate.error ?? enrichActions.resolve.error ?? null
  };
  const commercialEdit: CommercialEditHandlers = {
    save: (patch) => mutation.mutate({ productId: id, patch }),
    pending: mutation.isPending,
    error: mutation.error ?? null,
    saved: mutation.isSuccess
  };
  const mediaEdit: MediaEditHandlers = {
    add: (patch) => addMedia.mutate({ productId: id, patch }),
    update: (mediaId, patch) => updateMedia.mutate({ productId: id, mediaId, patch }),
    remove: (mediaId) => removeMedia.mutate({ productId: id, mediaId }),
    pending: addMedia.isPending || updateMedia.isPending || removeMedia.isPending,
    error: addMedia.error ?? updateMedia.error ?? removeMedia.error ?? null,
    saved: addMedia.isSuccess || updateMedia.isSuccess || removeMedia.isSuccess
  };
  return (
    <CatalogBoundary>
      {(dataset: CatalogDataset) => (
        <ProdutoDetalheView dataset={dataset} productId={id} commercialEdit={commercialEdit} mediaEdit={mediaEdit} aiEnrich={aiEnrich} />
      )}
    </CatalogBoundary>
  );
}
